// Pure-function standings derivers — compute live 3v3 group standings + 1v1
// Swiss standings from match outcomes (state.matches{3v3,1v1} + state.matchResults).
//
// Match-points (BO2 quals):
//   2 round wins → 3 match points
//   1 round win  → 1 match point
//   0 round wins → 0 match points
// Forfeit: scored as a 2-0 sweep of a BO2 — surviving side gets 3 match points +
//   400 VP (2 forfeited rounds × 200 VP each); the forfeiting side gets 0 match
//   points + 0 VP.
//
// Tiebreaker chains differ by event:
//
//   3v3 groups (makeComparator3v3):
//     1. RP (match points; penalty-adjusted, kept UNFLOORED for sorting)  desc
//     2. Head-to-head — ONLY for a strict 2-team tie: if A beat B, A ranks higher.
//        A 3+-way tie skips H2H entirely (it can be circular) and goes to step 3.
//     3. VP differential (own VP − opponent VP across 3v3 quals)    desc
//     4. If still tied → mark `needsTiebreaker` (operator runs a BO3)
//     5. original group seed order (input order, NOT alphabetical) — display-only
//
//   1v1 Swiss (makeComparator1v1):
//     1. RP                                                          desc
//     2. Head-to-head
//     3. Cumulative score: sum of running totals after each match   desc
//     4. Median Buchholz: sum of opponents' RPs minus highest+lowest desc
//     5. If still tied → mark `needsTiebreaker` (operator runs a BO3)
//     6. team id
//
// Mirror the 3v3 chain in the qual_standings_3v3 SQL view (used for bracket
// seeding) and the 1v1 chain in qual_standings_1v1. Step 5 (seed-order fallback)
// is display-only — genuinely-tied teams go to a BO3, so the SQL view keeps its
// deterministic team-id fallback. (If that view doesn't already subtract the 3v3
// penalty, the seeding it produces won't match this penalty-adjusted display.)

import type {
  CheckIn, Match, MatchEvent, RoundResult, StandingsRow3v3, Group3v3, SwissRow, Swiss1v1, Team, TeamConfig, TeamId,
} from '../types';

interface PerTeamTotals {
  team: TeamId;
  matchesPlayed: number;
  w: number;   // matches where this team got more match points than opponent
  l: number;   // matches where this team got fewer match points than opponent
  t: number;   // matches where both teams got equal match points (W+L=1-1 OR T+T=0-0)
  rp: number;  // total match points (= sum of per-match points)
  op: number;  // offensive points (sum of own scores across rounds)
  dp: number;  // defensive points (sum of opponent scores)
  opponents: TeamId[];                         // real opponents — used for median Buchholz
  byeCount: number;                            // # of byes received — folded in via self-pair
  resultByOpp: Map<TeamId, 'w' | 't' | 'l'>;   // for head-to-head
  perMatchPoints: number[];                    // chronological list, for cumulative
}

const COMPLETED_LIKE = new Set(['completed', 'forfeit_v', 'forfeit_iv']);
const FORFEIT_LIKE   = new Set(['forfeit_v', 'forfeit_iv']);

// Quals-only kinds. ARCTIC + EC + playoff matches must NOT contribute to
// qual standings — `aggregate` filters them out. (NB: src/state/standings.ts
// only sees Match objects, which don't carry `kind`. The caller is expected
// to pass already-filtered matches3v3 / matches1v1; we additionally reject
// matches whose id starts with a playoff prefix.)
const PLAYOFF_PREFIX = /^[UE]_/;
const isPlayoffId = (id: string) => PLAYOFF_PREFIX.test(id);

function aggregate(matches: Match[], results: Record<string, RoundResult[]>): Map<TeamId, PerTeamTotals> {
  const totals = new Map<TeamId, PerTeamTotals>();
  const ensure = (team: TeamId): PerTeamTotals => {
    let row = totals.get(team);
    if (!row) {
      row = {
        team, matchesPlayed: 0, w: 0, l: 0, t: 0, rp: 0, op: 0, dp: 0,
        opponents: [], byeCount: 0, resultByOpp: new Map(), perMatchPoints: [],
      };
      totals.set(team, row);
    }
    return row;
  };

  // matches array is already chronologically ordered by SupabaseStore.fetchAll
  // (DB query sorts by scheduled_at). The mock fixtures preserve this too.
  for (const m of matches) {
    if (isPlayoffId(m.id)) continue;        // playoff matches don't count toward quals

    // Swiss bye: red team is automatically credited with a full win.
    // For Buchholz, we use the "self-pair" convention — the bye opponent is
    // treated as having the team's own RP. We track byeCount here and fold
    // it into the opponent-RP list inside medianBuchholzOf, so the team's
    // current rp is included once per bye (correctly reflecting that a bye
    // shouldn't penalize a team's tiebreaker).
    if (m.bye === true && m.red) {
      const team = ensure(m.red);
      team.matchesPlayed += 1;
      team.w += 1;
      team.rp += 3;
      team.byeCount += 1;
      team.perMatchPoints.push(3);
      continue;
    }

    if (!m.red || !m.blue) continue;        // ARCTIC / EC placeholders
    if (!COMPLETED_LIKE.has(m.redS) || !COMPLETED_LIKE.has(m.blueS)) continue;

    const red = ensure(m.red);
    const blue = ensure(m.blue);
    red.matchesPlayed += 1;
    blue.matchesPlayed += 1;
    red.opponents.push(m.blue);
    blue.opponents.push(m.red);

    let redMP: number;
    let blueMP: number;
    if (FORFEIT_LIKE.has(m.redS)) {
      redMP = 0; blueMP = 3;
      // A forfeit records no rounds, so op/dp must be set here explicitly: a BO2
      // forfeit is a 2-0 sweep = 2 rounds × 200 VP = 400 VP to the surviving
      // side, 0 to the forfeiter. Mirror this in the qual_standings_3v3 SQL view
      // — otherwise a forfeit adds 0 VP and vanishes from the VP-differential
      // tiebreak.
      blue.op += 400; red.dp += 400;
    } else if (FORFEIT_LIKE.has(m.blueS)) {
      redMP = 3; blueMP = 0;
      red.op += 400; blue.dp += 400;
    } else {
      const rounds = results[m.id] ?? [];
      let redRounds = 0, blueRounds = 0;
      for (const round of rounds) {
        if (round.winner === 'red')  redRounds  += 1;
        else if (round.winner === 'blue') blueRounds += 1;
        red.op  += round.redScore;
        red.dp  += round.blueScore;
        blue.op += round.blueScore;
        blue.dp += round.redScore;
      }
      const matchPoints = (rw: number) => rw === 2 ? 3 : rw === 1 ? 1 : 0;
      redMP  = matchPoints(redRounds);
      blueMP = matchPoints(blueRounds);
    }

    red.rp  += redMP;
    blue.rp += blueMP;
    red.perMatchPoints.push(redMP);
    blue.perMatchPoints.push(blueMP);

    // W/L/T by point comparison: T+T (0 vs 0) and W+L (1 vs 1) are both T.
    if (redMP > blueMP)  red.w += 1;
    else if (redMP < blueMP) red.l += 1;
    else                 red.t += 1;
    if (blueMP > redMP)  blue.w += 1;
    else if (blueMP < redMP) blue.l += 1;
    else                 blue.t += 1;

    const cmp = (mine: number, opp: number): 'w' | 'l' | 't' =>
      mine > opp ? 'w' : mine < opp ? 'l' : 't';
    red.resultByOpp.set(m.blue, cmp(redMP, blueMP));
    blue.resultByOpp.set(m.red, cmp(blueMP, redMP));
  }

  return totals;
}

// Cumulative score = sum of the team's running RP total after each match
// played, in chronological order. Favors teams that scored points early.
function cumulativeOf(tot: PerTeamTotals | undefined): number {
  if (!tot) return 0;
  let running = 0;
  let cumulative = 0;
  for (const mp of tot.perMatchPoints) {
    running += mp;
    cumulative += running;
  }
  return cumulative;
}

// Median Buchholz = sum of opponents' RPs after dropping the single highest
// and single lowest. With ≤2 opponents, the cut is meaningless — we sum all.
//
// Self-pair byes: each bye received contributes the team's OWN current RP
// to the opp-RP list. This matches the standard chess Swiss convention
// (the bye opponent is treated as having the team's same record, so a
// bye doesn't penalize the team's Buchholz). With this rule, the list
// length is always equal to matchesPlayed.
function medianBuchholzOf(tot: PerTeamTotals | undefined, totals: Map<TeamId, PerTeamTotals>): number {
  if (!tot) return 0;
  const oppRps = tot.opponents.map((id) => totals.get(id)?.rp ?? 0);
  for (let i = 0; i < tot.byeCount; i++) oppRps.push(tot.rp);
  if (oppRps.length <= 2) return oppRps.reduce((s, x) => s + x, 0);
  oppRps.sort((a, b) => a - b);
  return oppRps.slice(1, -1).reduce((s, x) => s + x, 0);
}

// Head-to-head: −1 if a beat b, +1 if b beat a, null if no decisive pairwise
// result (no match, a tie, or a transitive cycle — those fall through).
function headToHead(totals: Map<TeamId, PerTeamTotals>, aTeam: TeamId, bTeam: TeamId): number | null {
  const r = totals.get(aTeam)?.resultByOpp.get(bTeam);
  if (r === 'w') return -1;
  if (r === 'l') return 1;
  return null;
}

// 1v1 Swiss comparator: RP → head-to-head → cumulative score → median Buchholz
// → team id. Returns negative when `a` ranks higher. Genuinely-equal pairs are
// flagged needsTiebreaker in a post-sort pass.
function makeComparator1v1(totals: Map<TeamId, PerTeamTotals>) {
  return function compareTiebreakers(a: { team: TeamId; rp: number; cumulativeScore: number; medianBuchholz: number }, b: typeof a): number {
    if (a.rp !== b.rp) return b.rp - a.rp;
    const h2h = headToHead(totals, a.team, b.team);
    if (h2h !== null) return h2h;
    if (a.cumulativeScore !== b.cumulativeScore) return b.cumulativeScore - a.cumulativeScore;
    if (a.medianBuchholz !== b.medianBuchholz) return b.medianBuchholz - a.medianBuchholz;
    return a.team.localeCompare(b.team);
  };
}

// 3v3 group comparator: RP → head-to-head → VP differential (op − dp) → group seed order.
// Distinct from 1v1: 3v3 uses VP differential as the numeric tiebreaker (cumulative
// score and median Buchholz are not used), and anything still tied after VP
// differential goes to an ad-hoc BO3 (flagged by markUnresolvedTies3v3).
//
// Head-to-head only breaks a strict TWO-team tie. Once 3+ teams share an RP a
// head-to-head can be circular (A beat B, B beat C, C beat A), so by rule we skip
// it and let VP differential decide. `rpGroupSize` maps an RP value → how many
// teams in this group hold it, gating the H2H step. Keeping H2H to a unique pair
// also keeps the comparator transitive (no cycle can reach Array.sort). Mirror
// this in the qual_standings_3v3 SQL view.
function makeComparator3v3(totals: Map<TeamId, PerTeamTotals>, rpGroupSize: Map<number, number>, seedRank: Map<TeamId, number>) {
  return function compareTiebreakers(a: { team: TeamId; rp: number; op: number; dp: number }, b: typeof a): number {
    if (a.rp !== b.rp) return b.rp - a.rp;
    if (rpGroupSize.get(a.rp) === 2) {
      const h2h = headToHead(totals, a.team, b.team);
      if (h2h !== null) return h2h;
    }
    const aDiff = a.op - a.dp, bDiff = b.op - b.dp;
    if (aDiff !== bDiff) return bDiff - aDiff;
    // Final fallback is DISPLAY-only (genuinely-tied teams go to a BO3): bias to
    // the original group seed order rather than alphabetical team id.
    return (seedRank.get(a.team) ?? 0) - (seedRank.get(b.team) ?? 0);
  };
}

// 3v3: flag adjacent rows still tied after RP + head-to-head + VP differential —
// the operator resolves these with an ad-hoc BO3. Only ties affecting the top-3
// positions matter (rank 1↔2 decides seed order, 2↔3 decides who advances), so
// gate to the first `cutoff` pairs. Head-to-head only counts as "resolved" for a
// strict two-team tie (mirrors makeComparator3v3); in a 3+-way tie VP differential
// alone decides, so a decisive pairwise H2H between two of them does NOT clear the
// flag — only an equal VP differential leaves them genuinely tied.
function markUnresolvedTies3v3(rows: StandingsRow3v3[], totals: Map<TeamId, PerTeamTotals>, rpGroupSize: Map<number, number>, cutoff = 2): void {
  for (let i = 0; i < rows.length - 1 && i < cutoff; i++) {
    const a = rows[i], b = rows[i + 1];
    if (a.rp !== b.rp) continue;
    if ((a.op - a.dp) !== (b.op - b.dp)) continue;
    if (rpGroupSize.get(a.rp) === 2 && headToHead(totals, a.team, b.team) !== null) continue;
    a.needsTiebreaker = true;
    b.needsTiebreaker = true;
  }
}

// Mark adjacent rows that are still tied after the full tiebreaker chain
// (rp + cumulative + median match AND H2H didn't decide). Pairwise; covers
// 3+ way cyclic ties because every consecutive pair in the cycle gets flagged.
//
// `topNCutoff` (optional): only flag ties involving rows at index < cutoff.
// Used by 1v1 standings to gate the BO3 marker to ties that matter for the
// playoff cutoff (top 8). Passing 8 means a tie between ranks 8 and 9 is
// flagged (the boundary case decides who makes elims), but a tie between
// ranks 9 and 10 is not.
function markUnresolvedTies<T extends { team: TeamId; rp: number; cumulativeScore: number; medianBuchholz: number; needsTiebreaker?: boolean }>(
  rows: T[],
  totals: Map<TeamId, PerTeamTotals>,
  options: { topNCutoff?: number } = {},
): void {
  const cutoff = options.topNCutoff;
  for (let i = 0; i < rows.length - 1; i++) {
    if (cutoff != null && i >= cutoff) break;
    const a = rows[i];
    const b = rows[i + 1];
    if (a.rp !== b.rp) continue;
    if (a.cumulativeScore !== b.cumulativeScore) continue;
    if (a.medianBuchholz !== b.medianBuchholz) continue;
    const aResult = totals.get(a.team)?.resultByOpp.get(b.team);
    if (aResult === 'w' || aResult === 'l') continue;
    a.needsTiebreaker = true;
    b.needsTiebreaker = true;
  }
}

// 3v3: per-group round-robin. Each group is sorted independently using the
// shared tiebreaker chain. Top 2 per group get advance: true (8 playoff seeds
// total). Optional `checkIn` arg subtracts the 3v3 points penalty from RP
// (floored at 0).
export function computeStandings3v3(
  matches: Match[],
  results: Record<string, RoundResult[]>,
  teams: Team[],
  checkIn?: CheckIn,
  /** Per-team group override (Captain's-Meeting-driven). When provided,
   *  `teamConfig[id].group` wins over the static `team.group` from the
   *  roster. Wired so the standings page reflects whatever ops set in
   *  the admin screen (or whatever the server synced from teams.group_3v3
   *  after Quals Live), not the seed defaults. Falls back to t.group if
   *  not provided (test fixtures, legacy callers). */
  teamConfig?: TeamConfig,
): Group3v3[] {
  const totals = aggregate(matches, results);

  const byGroup = new Map<string, Team[]>();
  for (const t of teams) {
    if (!t.events.includes('3v3')) continue;
    const group = teamConfig?.[t.id]?.group ?? t.group;
    if (!group) continue;
    const list = byGroup.get(group) ?? [];
    list.push(t);
    byGroup.set(group, list);
  }

  const groups: Group3v3[] = [];
  for (const groupName of Array.from(byGroup.keys()).sort()) {
    const teamsInGroup = byGroup.get(groupName)!;
    const rows: StandingsRow3v3[] = teamsInGroup.map((t) => {
      const tot = totals.get(t.id);
      const earnedRP = tot?.rp ?? 0;
      const penalty = checkIn?.[t.id]?.pointsPenalty3v3 ?? 0;
      return {
        rank: 0,
        team: t.id,
        w: tot?.w ?? 0,
        l: tot?.l ?? 0,
        t: tot?.t ?? 0,
        op: tot?.op ?? 0,
        dp: tot?.dp ?? 0,
        // Penalty-adjusted but NOT yet floored — the penalty must count for the
        // sort + tie detection even when it pushes the score below 0. Floored
        // for display further down, once sorting + BO3 flags are settled.
        rp: earnedRP - penalty,
        cumulativeScore: cumulativeOf(tot),
        medianBuchholz: medianBuchholzOf(tot, totals),
        penalty: penalty > 0 ? penalty : undefined,
      };
    });
    // How many teams share each RP within this group — gates the H2H step so it
    // only fires on a strict two-team tie. Keyed on the UNFLOORED RP so a penalty
    // that drops one team below another isn't masked into a false tie at 0.
    const rpGroupSize = new Map<number, number>();
    for (const r of rows) rpGroupSize.set(r.rp, (rpGroupSize.get(r.rp) ?? 0) + 1);

    // Display fallback for genuinely-tied teams: original group seeding (the order
    // teams were passed in for this group), not alphabetical.
    const seedRank = new Map(teamsInGroup.map((tm, i): [TeamId, number] => [tm.id, i]));
    rows.sort(makeComparator3v3(totals, rpGroupSize, seedRank));
    rows.forEach((row, i) => { row.rank = i + 1; });
    rows.forEach((row, i) => { if (i < 2) row.advance = true; });
    // Top-3 ties that survive RP + head-to-head + VP differential go to an
    // ad-hoc BO3 (rank 1↔2 decides seed order, 2↔3 decides who advances).
    markUnresolvedTies3v3(rows, totals, rpGroupSize);
    // Floor the DISPLAYED RP at 0 now that sorting + tie flags (which need the
    // true penalty-adjusted value) are settled.
    rows.forEach((row) => { if (row.rp < 0) row.rp = 0; });
    groups.push({ name: groupName, rows });
  }

  return groups;
}

// 1v1 Swiss: flat standings across all 1v1-eligible teams. Same tiebreaker
// chain as 3v3 (RP → H2H → cumulative → median → BO3 marker). `totalRounds`
// is required (no default) so call sites pass the shared SWISS_TOTAL_ROUNDS
// constant from src/state/swiss.ts and stay in sync.
export function computeStandings1v1(
  matches: Match[],
  results: Record<string, RoundResult[]>,
  teams: Team[],
  totalRounds: number,
): Swiss1v1 {
  const totals = aggregate(matches, results);
  const compare = makeComparator1v1(totals);

  const eligibleTeams = teams.filter((t) => t.events.includes('1v1'));
  // Once the Swiss has started, only teams that ENTERED a qual match (any status
  // — played, pending, or byed) are participants; a registered no-show that never
  // got a match is excluded, so it can't sit in the standings or be pulled into a
  // later round's pairing/bye (which is how a no-show drew a phantom bye). Before
  // round 1 (no qual matches yet) the full eligible roster is shown so it can be
  // paired. E<digit> = a 1v1 qual (E1, E12…); playoffs are E_-prefixed.
  const entered = new Set<string>();
  for (const m of matches) {
    if (!/^E\d/.test(m.id)) continue;
    if (m.red) entered.add(m.red);
    if (m.blue) entered.add(m.blue);
  }
  const activeTeams = entered.size > 0
    ? eligibleTeams.filter((t) => entered.has(t.id))
    : eligibleTeams;

  const rows: SwissRow[] = activeTeams.map((t) => {
    const tot = totals.get(t.id);
    return {
      rank: 0,
      team: t.id,
      w: tot?.w ?? 0,
      l: tot?.l ?? 0,
      t: tot?.t ?? 0,
      rp: tot?.rp ?? 0,
      cumulativeScore: cumulativeOf(tot),
      medianBuchholz: medianBuchholzOf(tot, totals),
    };
  });

  rows.sort(compare);
  rows.forEach((row, i) => { row.rank = i + 1; });
  rows.forEach((row, i) => { if (i < 8) row.advance = true; });

  const round = Math.min(
    totalRounds,
    Math.max(0, ...Array.from(totals.values()).map((t) => t.matchesPlayed)),
  );

  // BO3 marker: only meaningful at end of the FINAL Swiss round, and only
  // for ties at or above the playoff cutoff (rank ≤ 8 — at-least-tied-for-8).
  // Mid-Swiss ties resolve naturally in subsequent rounds; ranks below 8
  // don't affect playoff eligibility.
  if (round >= totalRounds) {
    markUnresolvedTies(rows, totals, { topNCutoff: 8 });
  }

  return { round, totalRounds, rows };
}

// Cross-group seeding for the 3v3 playoff bracket. Top 1 from each group
// becomes seeds 1–4; top 2 from each group becomes seeds 5–8. Within each
// tier, ranked by the same tiebreaker chain.
// Group-letter seeding: seeds are assigned by group order (A → D), NOT by
// cross-group record. With groups arriving from computeStandings3v3 in
// alphabetical order:
//   1 = A1, 2 = B1, 3 = C1, 4 = D1
//   5 = A2, 6 = B2, 7 = C2, 8 = D2
// The 3v3 bracket (generate_bracket, migration 97) swaps UB2<->UB4 vs the
// textbook pairing → 1v8 / 3v6 / 2v7 / 4v5, so the round-1 matchups are
// A1↔D2, C1↔B2, B1↔C2, D1↔A2 AND each group's two teams land on opposite
// halves — no same-group rematch before the UB final (textbook 1v8/4v5/2v7/3v6
// would let A1/A2 or D1/D2 meet in round 2). Group A is always the top-seeded
// group; D the lowest. Inter-group records don't reshuffle.
export function computeTopEight3v3(groups: Group3v3[]): StandingsRow3v3[] {
  const groupWinners = groups.map((g) => g.rows[0]).filter(Boolean);
  const groupSeconds = groups.map((g) => g.rows[1]).filter(Boolean);
  const seeds = [...groupWinners, ...groupSeconds].slice(0, 8);
  return seeds.map((row, i) => ({ ...row, rank: i + 1 }));
}

// ============================================================================
// Engineer Challenge standings
// ============================================================================
//
// EC is a single-team timed-task event. Each team plays one EC match
// composed of up to 3 rounds; each round scores a number of points
// (red_score on the match_rounds row — blue_score is unused, blue_team
// is null). The final score is the AVERAGE of the team's 2 HIGHEST
// round scores (drop the lowest).
//
// Tiebreaker: lighter Engineer robot wins. Weight is recorded at S1
// inspection (robot_inspection_station.weight_kg) and surfaced via
// state.robotWeight[team]['ec']['Engineer'].
//
// Teams without any recorded round get rank 0 / final 0; they sort to
// the bottom of the table. Teams without a recorded weight get
// weightKg=NaN; they lose all weight-tiebreak comparisons (i.e. a
// known-weight team beats them).
import type { ECRun, ECStandingsRow, RobotWeight } from '../types';

export function computeStandingsEC(
  ecRuns: ReadonlyArray<ECRun>,
  results: Record<string, ReadonlyArray<RoundResult>>,
  robotWeight: RobotWeight,
  teams: ReadonlyArray<Team>,
): ECStandingsRow[] {
  // Restrict to teams registered for EC. Use teams as the source so the
  // standings include teams that haven't played yet (final score 0,
  // rank goes to the bottom) — keeps the table at a stable height.
  const ecTeams = teams.filter((t) => t.events.includes('EC'));

  const rows = ecTeams.map<Omit<ECStandingsRow, 'rank'>>((team) => {
    // Find this team's EC run, if any (each team plays once).
    const run = ecRuns.find((e) => e.team === team.id);
    const rounds = run ? (results[run.id] ?? []) : [];
    const roundScores = rounds.map((r) => r.redScore ?? 0);

    // Best-2-of-3: sort descending, take top 2, average. With <2 rounds
    // played, average what's there; with 0, score is 0.
    let finalScore = 0;
    if (roundScores.length > 0) {
      const sorted = roundScores.slice().sort((a, b) => b - a);
      const top2 = sorted.slice(0, 2);
      finalScore = top2.reduce((s, v) => s + v, 0) / top2.length;
    }

    // Engineer weight at S1 (kg). NaN if not recorded. Engineer has no
    // backup instance, so the 'primary' slot holds the recorded weight.
    const w = robotWeight[team.id]?.['ec']?.['Engineer']?.['primary']?.kg;
    const weightKg = typeof w === 'number' ? w : NaN;

    return { team: team.id, roundScores, finalScore, weightKg };
  });

  // Sort: highest final score first; ties broken by lighter weight
  // (smaller kg ranks higher); known weight beats NaN weight; final
  // tiebreaker is team id for deterministic order.
  rows.sort((a, b) => {
    if (b.finalScore !== a.finalScore) return b.finalScore - a.finalScore;
    const aHasW = !Number.isNaN(a.weightKg);
    const bHasW = !Number.isNaN(b.weightKg);
    if (aHasW !== bHasW) return aHasW ? -1 : 1;
    if (aHasW && bHasW && a.weightKg !== b.weightKg) return a.weightKg - b.weightKg;
    return a.team.localeCompare(b.team);
  });

  // Mark teams whose score AND weight match the next/prev row — those
  // are genuinely unbreakable from the data alone and need a manual
  // tiebreaker (extra round, judge call, etc.).
  const out: ECStandingsRow[] = rows.map((r, i) => {
    const prev = rows[i - 1];
    const next = rows[i + 1];
    const tieToPrev = prev && prev.finalScore === r.finalScore
      && (Number.isNaN(prev.weightKg) ? Number.isNaN(r.weightKg)
        : !Number.isNaN(r.weightKg) && prev.weightKg === r.weightKg);
    const tieToNext = next && next.finalScore === r.finalScore
      && (Number.isNaN(next.weightKg) ? Number.isNaN(r.weightKg)
        : !Number.isNaN(r.weightKg) && next.weightKg === r.weightKg);
    return {
      ...r,
      rank: i + 1,
      needsTiebreaker: tieToPrev || tieToNext || undefined,
    };
  });

  return out;
}

export type { MatchEvent };
